import { Box, Switch, FormControlLabel, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { describeError, useToast } from "../toast";

/**
 * The rooms half of one connection's card (`MEWS-4`). The mapping itself is
 * edited with the unit, in Unit setup (`RoomsEditor`, its PMS column and drawer
 * field), since 2026-10-28; its counts and the way there left this card the
 * same day (Sigge): the hotel maps on Unit setup, not here.
 *
 * Vendor-neutral, like the panel around it: it appears for any adapter that
 * lists units. Nothing here names Mews.
 *
 * What it still owns:
 * - **Who checks guests in and out.** Off (the default), the vendor's stays
 *   answer "who do I bill?" and nothing else. On, the vendor is the one trigger
 *   for this hotel's check-ins and check-outs, the TVs included
 *   (`occupancyDriver.ts`) — a decision made per hotel, after the mapping.
 */

interface Props {
  premiseId: string;
  vendor: string;
  displayName: string;
  hasCredentials: boolean;
  /** The connection's config: the switch rewrites one key of it. */
  config?: Record<string, string>;
  /** The adapter's stays drive occupancy only when this hotel opts in. */
  occupancyOptIn?: boolean;
  onSaved?: () => void | Promise<void>;
}

/** Mirrors the server's `DRIVES_OCCUPANCY_KEY`. */
const DRIVES_OCCUPANCY_KEY = "drivesOccupancy";

export default function LinkRoomsSection(props: Props) {
  const {
    premiseId,
    vendor,
    displayName,
    hasCredentials,
    config = {},
    occupancyOptIn = false,
    onSaved,
  } = props;
  const drives = config[DRIVES_OCCUPANCY_KEY] === "on";
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const [busy, setBusy] = useState(false);

  async function setDrives(on: boolean) {
    setBusy(true);
    try {
      const res = await fetch(
        `/api/cms/v1/premise/${premiseId}/link/connections`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "content-type": "application/json" },
          // The whole config with one key replaced: a PUT of the switch alone
          // would wipe the services the TV shows.
          body: JSON.stringify({
            vendor,
            config: { ...config, [DRIVES_OCCUPANCY_KEY]: on ? "on" : "" },
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(
        on
          ? t("linkRoomsSection.drivesOn", { name: displayName })
          : t("linkRoomsSection.drivesOff", { name: displayName })
      );
      await onSaved?.();
    } catch (e) {
      toast.error(describeError(t, "changeOccupancyDriver", e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box data-testid={`link-rooms-${vendor}`}>
      <Typography variant="subtitle2">
        {t("linkRoomsSection.title", { name: displayName })}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {t("linkRoomsSection.intro", { name: displayName })}
        {occupancyOptIn && !drives && (
          <> {t("linkRoomsSection.notDriving", { name: displayName })}</>
        )}
      </Typography>

      {occupancyOptIn && (
        <Box sx={{ my: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={drives}
                disabled={busy || !hasCredentials}
                onChange={(e) => void setDrives(e.target.checked)}
                inputProps={
                  {
                    "data-testid": `link-drives-occupancy-${vendor}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={t("linkRoomsSection.drivesLabel", { name: displayName })}
          />
          {drives && (
            <Typography variant="caption" color="text.secondary" component="p">
              {t("linkRoomsSection.drivesHelp", { name: displayName })}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
